const SENTINEL_MARKER = 'documentDraftGuard';
const EXPECTED_POP_TIMEOUT_MS = 1500;

let isPopStateExpected = false;
let expectedPopTimer: ReturnType<typeof setTimeout> | null = null;

const clearExpectedPop = (): void => {
	isPopStateExpected = false;

	if (expectedPopTimer !== null) {
		clearTimeout(expectedPopTimer);
		expectedPopTimer = null;
	}
};

const expectPopState = (): void => {
	clearExpectedPop();
	isPopStateExpected = true;
	// A move past the start of the history fires no `popstate`: the flag must not outlive it.
	expectedPopTimer = setTimeout(clearExpectedPop, EXPECTED_POP_TIMEOUT_MS);
};

/**
 * The history side of the document draft guard, shared by `useNavigationGuard` and
 * `DiscardChangesDialog`. One marked sentinel entry sits above the screen's own entry while a draft is
 * modified; `history.go` and `popstate` are both asynchronous, so a « popstate expected » flag tells the
 * guard that a navigation it started itself is in flight.
 */
const documentHistoryGuard = Object.freeze({
	/** Pushes the marked entry unless the current one already is it, so it never stacks. */
	pushSentinel: (): void => {
		if (window.history.state?.[SENTINEL_MARKER] !== true) {
			window.history.pushState({ ...window.history.state, [SENTINEL_MARKER]: true }, '');
		}
	},

	isPopStateExpected: (): boolean => isPopStateExpected,

	/** Settles the flag once the awaited `popstate` has arrived. */
	consumePopState: (): void => {
		clearExpectedPop();
	},

	/** Steps back over the sentinel only: the draft was settled and the user stays on the screen. */
	removeSentinel: (): void => {
		if (isPopStateExpected || window.history.state?.[SENTINEL_MARKER] !== true) {
			return;
		}

		expectPopState();
		window.history.back();
	},

	/** Leaves the screen through the browser's back: over the sentinel and the screen's own entry. */
	leaveByHistory: (): void => {
		expectPopState();
		window.history.go(-2);
	},
});

export default documentHistoryGuard;
